import { inject } from '@angular/core';
import { PAGINATION_DEFAULT } from '@core/constants';
import { MY_COMPANY_USERS_PAGE_SIZE } from '@core/constants/my-company.constant';
import { COMPANY_USERS_PAGE_SIZE } from '@core/constants/company.constant';
import {
  CompanyFiltersModel,
  CountryModel,
  MeterRole,
  PartnerResponseModel,
  PartnerModel,
  CompanyPayloadModel,
  PartnerUsersResponseModel,
  WhiteLabelUpdateModel,
} from '@core/models';
import { CompanyService } from '@core/services';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, mergeMap, pipe, switchMap, tap } from 'rxjs';
import { pickBy } from 'lodash';

type CompanyState = {
  partnerNameCache: Record<string, string>;
  partnerSearchResults: PartnerModel[];
  isSearchingPartners: boolean;
  isLoading: boolean;
  isLoadingDetail: boolean;
  isLoadingWhiteLabel: boolean;
  isGettingUsers: boolean;
  usersByPartnerId: PartnerUsersResponseModel;
  customerData: PartnerResponseModel;
  companyDetail: PartnerModel;
  myCompany: PartnerModel;
  countries: CountryModel[];
  filter: CompanyFiltersModel;
  usersByMyCompany: PartnerUsersResponseModel;
  isLoadingLogo: boolean;
  myCompanyId: string;
  whiteLabelValue: any;
  isSavingDefaultMeterRole: boolean;
  errorSavingDefaultMeterRole: boolean;
};

const initialState: CompanyState = {
  partnerNameCache: {},
  partnerSearchResults: [],
  isSearchingPartners: false,
  isLoading: false,
  isLoadingDetail: false,
  isLoadingWhiteLabel: false,
  isLoadingLogo: false,
  isGettingUsers: false,
  isSavingDefaultMeterRole: false,
  errorSavingDefaultMeterRole: false,
  whiteLabelValue: undefined,
  customerData: {
    partners: [],
    numberOfItems: 0,
  },
  myCompanyId: '',
  companyDetail: {
    partnerId: '',
    partnerCity: '',
    partnerName: '',
    partnerPostalcode: '',
    partnerVat: '',
    partnerChamberOfCommerceNo: '',
    partnerCountry: '',
    partnerOid: '',
    partnerStatus: '',
    partnerType: '',
    partnerImage: '',
  },
  myCompany: {
    partnerId: '',
    partnerCity: '',
    partnerName: '',
    partnerPostalcode: '',
    partnerVat: '',
    partnerChamberOfCommerceNo: '',
    partnerCountry: '',
    partnerOid: '',
    partnerStatus: '',
    partnerType: '',
    partnerImage: '',
  },
  countries: [],
  filter: {
    limit: PAGINATION_DEFAULT.limit,
    offset: PAGINATION_DEFAULT.offset,
  },
  usersByPartnerId: { numberOfItems: 0, users: [] },
  usersByMyCompany: { numberOfItems: 0, users: [] },
};

export const CompanyStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      companyService = inject(CompanyService),
    ) => {
      const partnerFetchInProgress = new Set<string>();
      return {
        // Resets the partner search results (e.g. after a selection is made).
        clearPartnerSearch(): void {
          patchState(store, { partnerSearchResults: [], isSearchingPartners: false });
        },

        searchPartnersByNameMethod: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isSearchingPartners: true })),
            switchMap(partnerName => {
              // A blank query loads a default list of partners so the dropdown can be shown
              // on focus without requiring the user to type.
              const trimmed = partnerName?.trim();
              return companyService
                .getListCompaniesFilter({
                  limit: 10,
                  offset: 0,
                  ...(trimmed ? { partnerName: trimmed } : {}),
                })
                .pipe(
                  tapResponse({
                    next: (data: PartnerResponseModel) => {
                      patchState(store, {
                        partnerSearchResults: data.partners,
                        isSearchingPartners: false,
                      });
                    },
                    error: () => {
                      patchState(store, { partnerSearchResults: [], isSearchingPartners: false });
                    },
                  }),
                );
            }),
          ),
        ),

        getPartnerByIdMethod: rxMethod<string>(
          pipe(
            mergeMap(partnerId => {
              if (store.partnerNameCache()[partnerId] || partnerFetchInProgress.has(partnerId)) {
                return EMPTY;
              }
              partnerFetchInProgress.add(partnerId);
              return companyService.getCompanyDetail(partnerId).pipe(
                tapResponse({
                  next: (partner: PartnerModel) => {
                    partnerFetchInProgress.delete(partnerId);
                    patchState(store, {
                      partnerNameCache: {
                        ...store.partnerNameCache(),
                        [partnerId]: partner.partnerName,
                      },
                    });
                  },
                  error: () => {
                    partnerFetchInProgress.delete(partnerId);
                  },
                }),
              );
            }),
          ),
        ),

        refreshCompaniesMethod: rxMethod<void>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(() => {
              return companyService.getListCompaniesFilter(store.filter()).pipe(
                tapResponse({
                  next: (data: PartnerResponseModel) => {
                    patchState(store, { customerData: data, isLoading: false });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),

        getCompaniesMethod: rxMethod<number>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap((pageIndex: number) => {
              const filter: CompanyFiltersModel = {
                ...store.filter(),
                offset: pageIndex,
              };
              patchState(store, { filter });
              return companyService.getListCompaniesFilter(filter).pipe(
                tapResponse({
                  next: (data: PartnerResponseModel) => {
                    patchState(store, { customerData: data, isLoading: false });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),

        getCompaniesFilterMethod: rxMethod<CompanyPayloadModel>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(payload => {
              let filter = {
                ...store.filter(),
                ...payload,
                offset: initialState.filter.offset,
              };
              filter = pickBy(filter, (value: any) => value !== '') as any;
              patchState(store, { filter });
              return companyService.getListCompaniesFilter(filter).pipe(
                tapResponse({
                  next: (data: any) => {
                    patchState(store, {
                      customerData: data,
                      isLoading: false,
                      filter,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),

        getUsers: rxMethod<{
          partnerOid: string;
          offset?: number;
          limit?: number;
        }>(
          pipe(
            tap(() => patchState(store, { isGettingUsers: true })),
            switchMap(payload => {
              const pagination = {
                offset: payload.offset || 0,
                limit: payload.limit || COMPANY_USERS_PAGE_SIZE,
              };
              return companyService
                .getUsersByPartner(payload.partnerOid, pagination.offset, pagination.limit)
                .pipe(
                  tapResponse({
                    next: (data: any) => {
                      patchState(store, {
                        usersByPartnerId: data,
                        isGettingUsers: false,
                      });
                    },
                    error: () => {
                      patchState(store, { isGettingUsers: false });
                    },
                  }),
                );
            }),
          ),
        ),

        // My-company users (my-company page + app bootstrap). Separate switchMap
        // so the bootstrap fetch can't abort an in-flight partner users request.
        getMyCompanyUsers: rxMethod<{
          partnerOid: string;
          offset?: number;
          limit?: number;
        }>(
          pipe(
            switchMap(payload => {
              const pagination = {
                offset: payload.offset || 0,
                limit: payload.limit || MY_COMPANY_USERS_PAGE_SIZE,
              };
              return companyService
                .getUsersByPartner(payload.partnerOid, pagination.offset, pagination.limit)
                .pipe(
                  tapResponse({
                    next: (data: any) => {
                      patchState(store, { usersByMyCompany: data });
                    },
                    error: () => {},
                  }),
                );
            }),
          ),
        ),

        getCompanyDetailMethod: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isLoadingDetail: true })),
            switchMap(companyId => {
              return companyService.getCompanyDetail(companyId).pipe(
                tapResponse({
                  next: (data: PartnerModel) => {
                    patchState(store, { companyDetail: data, isLoadingDetail: false });
                  },
                  error: () => {
                    patchState(store, { isLoadingDetail: false });
                  },
                }),
              );
            }),
          ),
        ),

        getWhiteLabelMethod: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isLoadingWhiteLabel: true })),
            switchMap(companyId => {
              return companyService.getWhiteLabel(companyId).pipe(
                tapResponse({
                  next: (data: any) => {
                    patchState(store, {
                      whiteLabelValue: data,
                      isLoadingWhiteLabel: false,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoadingWhiteLabel: false });
                  },
                }),
              );
            }),
          ),
        ),

        updateWhiteLabelMethod: rxMethod<{
          whiteLabel: WhiteLabelUpdateModel;
          partnerOid: string;
        }>(
          pipe(
            tap(() => patchState(store, { isLoadingWhiteLabel: true })),
            switchMap(payload => {
              return companyService.updateWhiteLabel(payload.partnerOid, payload.whiteLabel).pipe(
                tapResponse({
                  next: () => {
                    patchState(store, { isLoadingWhiteLabel: false });
                  },
                  error: () => {
                    patchState(store, { isLoadingWhiteLabel: false });
                  },
                }),
              );
            }),
          ),
        ),

        getMyCompanyMethod: rxMethod<string>(
          pipe(
            tap(myCompanyId => patchState(store, { myCompanyId, isLoading: true })),
            switchMap(companyId => {
              return companyService.getCompanyDetail(companyId).pipe(
                tapResponse({
                  next: (data: PartnerModel) => {
                    patchState(store, { myCompany: data, isLoading: false });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),

        updateLogoMethod: rxMethod<{ avatar: any; partnerOid: string }>(
          pipe(
            tap(() => patchState(store, { isLoadingLogo: true })),
            switchMap(payload => {
              return companyService.updateCompanyLogo(payload.avatar, payload.partnerOid).pipe(
                tapResponse({
                  next: () => {
                    patchState(store, { isLoadingLogo: false });
                  },
                  error: () => {
                    patchState(store, { isLoadingLogo: false });
                  },
                }),
              );
            }),
          ),
        ),

        updateDefaultMeterRoleMethod: rxMethod<{ partnerOid: string; meterRole: MeterRole }>(
          pipe(
            tap(() =>
              patchState(store, {
                isSavingDefaultMeterRole: true,
                errorSavingDefaultMeterRole: false,
              }),
            ),
            switchMap(payload => {
              return companyService
                .updateDefaultMeterRole(payload.partnerOid, payload.meterRole)
                .pipe(
                  tapResponse({
                    next: () => {
                      patchState(store, {
                        isSavingDefaultMeterRole: false,
                        myCompany: {
                          ...store.myCompany(),
                          partnerDefaultMeterRole: payload.meterRole,
                        },
                      });
                    },
                    error: () => {
                      patchState(store, {
                        isSavingDefaultMeterRole: false,
                        errorSavingDefaultMeterRole: true,
                      });
                    },
                  }),
                );
            }),
          ),
        ),
      };
    },
  ),
);
